import { applyMongoFilter } from "@tellescope/utils";
import { extractRequestFromQueryKey } from "../../../keys";
import {
  convertTicketThreadCommentToInboxThread,
  dedupeInboxThreads,
  removeTicketThreadExcludedInboxThreadFields,
  threadIsNewerThanOldest,
} from "../../../utils";
import { CustomModelAutomaticMutationSideEffectConfigs } from "../types";

export const TICKET_THREAD_COMMENTS_MUTATION_SIDE_EFFECT_CONFIGS: CustomModelAutomaticMutationSideEffectConfigs<"ticket_thread_comments"> =
  {
    createOne: (_, response) => {
      if (!response) {
        return {};
      }

      const threadFromResponse = convertTicketThreadCommentToInboxThread(response);

      return {
        _otherModels: {
          inbox_threads: {
            load_threads: {
              predicate: (query) => {
                if (!threadIsNewerThanOldest(query.state.data, threadFromResponse)) {
                  return false;
                }

                const request = extractRequestFromQueryKey(query.queryKey);
                const filteredMdbFilter = removeTicketThreadExcludedInboxThreadFields(request?.mdbFilter ?? {});
                return applyMongoFilter(threadFromResponse, filteredMdbFilter);
              },
            },
          },
        },
      };
    },
    createSome: (_, response) => {
      if (!response) {
        return {};
      }

      const threadsFromResponse = dedupeInboxThreads(response.created.map(convertTicketThreadCommentToInboxThread));

      return {
        _otherModels: {
          inbox_threads: {
            load_threads: {
              predicate: (query) => {
                if (threadsFromResponse.every((thread) => !threadIsNewerThanOldest(query.state.data, thread))) {
                  return false;
                }

                const request = extractRequestFromQueryKey(query.queryKey);
                const filteredMdbFilter = removeTicketThreadExcludedInboxThreadFields(request?.mdbFilter ?? {});
                return threadsFromResponse.some((thread) => applyMongoFilter(thread, filteredMdbFilter));
              },
              request: {
                autobuild: true,
              },
              exact: false,
            },
          },
        },
      };
    },
    updateOne: (_, response) => {
      if (!response) {
        return {};
      }

      const threadFromResponse = convertTicketThreadCommentToInboxThread(response);

      return {
        _otherModels: {
          inbox_threads: {
            load_threads: {
              predicate: (query) => {
                // Check if message is already in preview first - if so, always invalidate
                const isInPreview = query.state.data?.pages.some((page) =>
                  page.threads.some((thread) => thread.type === "Email" && thread.threadId === response.id)
                );
                if (isInPreview) {
                  return true;
                }

                // If not in preview, check if it's newer than oldest cached thread
                if (!threadIsNewerThanOldest(query.state.data, threadFromResponse)) {
                  return false;
                }

                // Finally, check if it matches the filter
                const queryRequest = extractRequestFromQueryKey(query.queryKey);
                const filteredMdbFilter = removeTicketThreadExcludedInboxThreadFields(queryRequest?.mdbFilter ?? {});
                return applyMongoFilter(threadFromResponse, filteredMdbFilter);
              },
              request: {
                autobuild: true,
              },
              exact: false,
            },
          },
        },
      };
    },
    deleteOne: (messageIdFromRequest) => ({
      _otherModels: {
        inbox_threads: {
          load_threads: {
            predicate: (query) => {
              const isInPreview = query.state.data?.pages.some((page) =>
                page.threads.some((thread) => thread.type === "Email" && thread.threadId === messageIdFromRequest)
              );

              return !!isInPreview;
            },
            request: {
              autobuild: true,
            },
            exact: false,
          },
        },
      },
    }),
  };
